/**
 * Store Access Pattern Documentation
 * 
 * This file exports custom hooks that provide a clean, consistent API for accessing Zustand stores.
 * 
 * PREFERRED PATTERN: Use these custom hooks in components
 * - Components should import and use hooks like useProfile(), useModel(), etc.
 * - Hooks provide a curated API surface and better abstraction
 * - Example: const { profile, setPreference } = useProfile();
 * 
 * EXCEPTIONS: Direct store access is appropriate for:
 * 1. Performance-critical selectors (e.g., App.tsx root component)
 *    - Example: const isLoading = useAppStore(state => state.loading);
 * 2. Cross-store communication (stores accessing other stores)
 *    - Example: const lab = useProfileStore.getState().getSelectedLab();
 * 
 * ANTI-PATTERN TO AVOID:
 * - Don't mix hook and direct store usage in the same component
 * - Either use the hook OR use direct store access, not both
 */

// Import stores
import { useUserStore } from './userStore';
import { useProfileStore } from './profileStore';
import { useAppStore } from './appStore';
import { useModelStore } from './modelStore';
import { useSubscriptionStore } from './subscriptionStore';
import type { ApiServices } from '../hooks/useApi';
import type { Model, Checkpoint, Feature } from '../types/Model';

// Export stores
export { useUserStore } from './userStore';
export { useProfileStore, PREFERENCE_KEYS } from './profileStore';
export { useAppStore, type ThemeMode } from './appStore';
export { useModelStore } from './modelStore';
export { useSubscriptionStore } from './subscriptionStore';
export { useSocketStore, socketEvents } from './socketStore';

// Export initializers
export { initializeAuth, cleanupAuth } from './userStore';
export { initializeAppStore } from './appStore';

// Mimic useUser hook
export const useUser = () => {
    const state = useUserStore();
    return {
        user: state.user,
        firebaseUser: state.firebaseUser,
        token: state.token,
        loading: state.loading,
        error: state.error,
        signIn: state.signIn,
        signInWithGoogle: state.signInWithGoogle,
        signOut: state.signOut,
        signUp: state.signUp,
        sendPasswordResetEmail: state.sendPasswordResetEmail,
        getValidToken: state.getValidToken,
        ejectionStatus: state.ejectionStatus,
        setEjectionStatus: state.setEjectionStatus,
        clearEjectionStatus: state.clearEjectionStatus,
        reset: state.reset
    };
};

// Mimic useProfile hook
export const useProfile = () => {
    const state = useProfileStore();
    return {
        profile: state.profile,
        loading: state.loading,
        error: state.error,
        refreshProfile: (api?: ApiServices) => state.refreshProfile(api),
        setProfile: state.setProfile,
        updateProfile: state.updateProfile,
        getSelectedLab: state.getSelectedLab,
        setPreference: state.setPreference,
        getPreference: state.getPreference,
        reset: state.reset
    };
};

// Mimic useApp hook
export const useApp = () => {
    const state = useAppStore();
    return {
        ui: state.ui,
        setIsCondensed: state.setIsCondensed,
        setIsOpen: state.setIsOpen,
        setRefreshing: state.setRefreshing,
        setIsMobile: state.setIsMobile,
        referenceData: state.referenceData,
        refreshReferenceData: state.refreshReferenceData,
        loadReferenceData: state.loadReferenceData,
        getOutcomeBetFormat: state.getOutcomeBetFormat,
        getOutcomeType: state.getOutcomeType,
        getActiveDomains: state.getActiveDomains,
        reset: state.reset
    };
};

// Mimic useAppUI hook
export const useAppUI = () => {
    const state = useAppStore();
    return {
        ...state.ui.sidebar,
        themeMode: state.ui.themeMode,
        refreshing: state.ui.refreshing,
        progressMessage: state.ui.progressMessage,
        appRefreshing: state.ui.appRefreshing,
        pageRefreshing: state.ui.pageRefreshing,
        suppressBusMessages: state.ui.suppressBusMessages,
        showMemoryMonitor: state.ui.showMemoryMonitor,
        showSocketDebug: state.ui.showSocketDebug,
        forceEnableModelActions: state.ui.forceEnableModelActions,
        settingsDialog: state.ui.settingsDialog,
        setIsCondensed: state.setIsCondensed,
        setIsOpen: state.setIsOpen,
        setRefreshing: state.setRefreshing,
        setProgressMessage: state.setProgressMessage,
        setAppRefreshing: state.setAppRefreshing,
        setPageRefreshing: state.setPageRefreshing,
        setSuppressBusMessages: state.setSuppressBusMessages,
        setShowMemoryMonitor: state.setShowMemoryMonitor,
        setShowSocketDebug: state.setShowSocketDebug,
        setForceEnableModelActions: state.setForceEnableModelActions,
        setThemeMode: state.setThemeMode,
        openSettingsDialog: state.openSettingsDialog,
        closeSettingsDialog: state.closeSettingsDialog,
        reset: state.reset
    };
};

// Mimic useReferenceData hook
export const useReferenceData = () => {
    const state = useAppStore();
    return {
        ...state.referenceData,
        refresh: (api?: ApiServices) => state.refreshReferenceData(api),
        getDomainById: state.getDomainById,
        getDomainByName: state.getDomainByName,
        getDomainNames: state.getDomainNames,
        getDomainName: state.getDomainName,
        getOutcomeById: state.getOutcomeById,
        getOutcomeName: state.getOutcomeName,
        getOutcomeGroupById: state.getOutcomeGroupById,
        getOutcomeGroupName: state.getOutcomeGroupName,
        getBettingStrategyByKey: state.getBettingStrategyByKey,
        getBettingStrategyName: state.getBettingStrategyName,
        reset: state.reset
    };
};

// Model hook - unified interface for model state and operations
export const useModel = () => {
    const state = useModelStore();

    return {
        // State
        loading: state.loading,
        errors: state.errors,

        // Core data loading
        loadModelsForLab: state.loadModelsForLab,
        loadModelDetails: state.loadModelDetails,

        // Convenience wrapper - auto-fetches lab if not provided
        loadModels: (api: ApiServices, labId?: string) => {
            const selectedLab = useProfileStore.getState().getSelectedLab();
            const effectiveLabId = labId || selectedLab?.id;
            if (!effectiveLabId) {
                console.warn('[useModel] loadModels: No lab ID provided or selected');
                return Promise.resolve();
            }
            return state.loadModelsForLab(api, effectiveLabId);
        },

        // Lab management
        setCurrentLab: state.setCurrentLab,

        // Model selection
        setCurrentModel: state.setCurrentModel,

        // Model CRUD operations
        createModel: state.createModel,
        updateModel: state.updateModel,
        updateModelStatus: state.updateModelStatus,
        deleteModel: state.deleteModel,
        publishModel: state.publishModel,

        // Version operations
        createModelVersion: state.createModelVersion,
        deleteModelVersion: state.deleteModelVersion,
        updateModelVersionStatus: state.updateModelVersionStatus,

        // Checkpoint operations
        createCheckpoint: state.createCheckpoint,
        cancelOperation: state.cancelOperation,
        loadVersionCheckpoints: state.loadVersionCheckpoints,
        isLoadingVersionCheckpoints: state.isLoadingVersionCheckpoints,
        revertToCheckpoint: state.revertToCheckpoint,

        // Feature operations
        createFeature: state.createFeature,
        updateFeature: state.updateFeature,
        deleteFeature: state.deleteFeature,
        importFeatures: state.importFeatures,

        // Selectors
        getModel: state.getModel,
        getCurrentModel: state.getCurrentModel,
        getModelsArray: state.getModelsArray,
        getModelsByLab: state.getModelsByLab,

        // Computed helpers
        getLatestVersion: state.getLatestVersion,
        getLatestCheckpoint: state.getLatestCheckpoint,
        isProcessing: state.isProcessing,
        hasUnpublishedChanges: state.hasUnpublishedChanges,

        // Helper methods for checkpoint data
        getCurrentOperation: (model: Model) => state.getLatestCheckpoint(model)?.operation,
        getLatestCheckpointStatistic: (model: Model, name: string) => {
            return state.getLatestCheckpoint(model)?.results?.statistics?.find((s: { name: string; value: string }) => s.name === name)?.value;
        },
        getLatestCheckpointStatCount: (model: Model) => {
            const checkpoint: Checkpoint | undefined = state.getLatestCheckpoint(model);
            if (!checkpoint?.features) return 0;
            let totalCount = 0;
            checkpoint.features.forEach((feature: Feature) => {
                if (feature.formula) {
                    const matches = feature.formula.match(/stat\(/g);
                    if (matches) totalCount += matches.length;
                }
            });
            return totalCount;
        },

        // Utility methods
        isModelBusy: state.isModelBusy,
        isModelCanceling: state.isModelCanceling,
        refreshModel: state.refreshModel,
        refreshModels: state.refreshModels,
        reset: state.reset,
    };
};

// Mimic useSubscription hook
export const useSubscription = () => {
    const state = useSubscriptionStore();
    return {
        // State
        currentSubscription: state.currentSubscription,
        products: state.products,
        invoices: state.invoices,
        selectedInvoice: state.selectedInvoice,
        hasMoreInvoices: state.hasMoreInvoices,
        prorationPreview: state.prorationPreview,
        isLoadingSubscription: state.isLoadingSubscription,
        isLoadingProducts: state.isLoadingProducts,
        isLoadingInvoices: state.isLoadingInvoices,
        isChangingPlan: state.isChangingPlan,
        isCanceling: state.isCanceling,
        isReactivating: state.isReactivating,
        isCreatingSession: state.isCreatingSession,
        isAddingAddon: state.isAddingAddon,
        isRemovingAddon: state.isRemovingAddon,
        error: state.error,

        // Actions
        fetchSubscription: state.fetchSubscription,
        fetchProducts: state.fetchProducts,
        fetchInvoices: state.fetchInvoices,
        fetchInvoiceDetails: state.fetchInvoiceDetails,
        createPortalSession: state.createPortalSession,
        createCheckoutSession: state.createCheckoutSession,
        changeSubscription: state.changeSubscription,
        previewChange: state.previewChange,
        cancelSubscription: state.cancelSubscription,
        reactivateSubscription: state.reactivateSubscription,
        addAddon: state.addAddon,
        removeAddon: state.removeAddon,
        previewAddonChange: state.previewAddonChange,

        // Computed
        hasActiveSubscription: state.hasActiveSubscription,
        isSubscriptionCanceled: state.isSubscriptionCanceled,
        getCurrentPlan: state.getCurrentPlan,
        hasAddon: state.hasAddon,
        getActiveAddons: state.getActiveAddons,
        hasTeamOutcomes: state.hasTeamOutcomes,
        hasPrivateModels: state.hasPrivateModels,

        // Trial helpers
        isInTrial: state.isInTrial,
        isTrialExpired: state.isTrialExpired,
        getTrialDaysRemaining: state.getTrialDaysRemaining,
        getTrialUrgencyLevel: state.getTrialUrgencyLevel,

        // Utility
        setError: state.setError,
        reset: state.reset
    };
};

// Legacy hook for backward compatibility
export const useSidebar = () => useAppUI(); 